import { ANIMATION_LIFECYCLE_EVENTS, NAVIGATION_ANIMATION_PHASES } from './lifecycle/animation_lifecycle_events'
import { getScopeRoot } from '../scope/scope'

/**
 * Navigation fade for drag-physics elements.
 *
 * Drag-physics elements carry `no-animation`, so `getColumnsToAnimate()`
 * leaves them out of the nav-animation column cascade — they never fade
 * with page transitions. This adds a plain opacity fade (no translate,
 * no stagger) synced to the transition: fade in on IN_STARTED, fade out
 * on OUT_STARTED. Skipped when the matching `navigation_transition_in` /
 * `navigation_transition_out` mode is `none`.
 *
 * Idempotency (per CLAUDE.md "Idempotent bind / sync"): this is a
 * stateless one-shot fade, so the guard is a per-element processed-flag.
 * `transition_in` is emitted from three sites, so IN_STARTED can fire
 * several times for ONE navigation — all carrying the same lifecycle
 * `transitionId`. Stamping each element with the id it was faded for
 * makes the repeat fires no-ops; a fresh element (next navigation, or a
 * physics twin clone) is unstamped and still fades.
 */

const PHYSICS_SELECTOR = '[data-lay-drag-physics="1"]'

// `[data-lay-drag-physics="1"]` matches the VISIBLE physics node — the
// original `.col` when physics isn't bound, the twin clone when it is
// (the bound original becomes a placeholder with the attr stripped).
// Scoped so a page transition doesn't fade physics inside an open
// overlay, and vice versa.
function physicsElementsIn(scope) {
	const root = (scope && getScopeRoot(scope)) || document
	return Array.prototype.slice.call(root.querySelectorAll(PHYSICS_SELECTOR))
}

// Per-element processed-flag: returns only the elements not yet faded
// for this `transitionId` in this direction, stamping them as it goes.
function takeUnfaded(els, flag, transitionId) {
	const fresh = []
	for (let i = 0; i < els.length; i++) {
		if (els[i][flag] === transitionId) continue
		els[i][flag] = transitionId
		fresh.push(els[i])
	}
	return fresh
}

function fadeIn(scope, transitionId) {
	const ld = window.layData
	if (ld.navigation_transition_in === 'none') return
	const els = takeUnfaded(physicsElementsIn(scope), '_layNavFadeInId', transitionId)
	if (els.length === 0) return
	anime.remove(els)
	// opacity:0 set synchronously — this runs inside the IN_STARTED
	// handler, before the scope is revealed/painted, so the element
	// can't flash at full opacity for a frame.
	for (let i = 0; i < els.length; i++) els[i].style.opacity = '0'
	anime({
		targets: els,
		opacity: [0, 1],
		duration: parseInt(ld.navigation_transition_in_duration, 10) || 400,
		easing: ld.navigation_transition_in_easing || 'easeOutSine',
		complete: function () {
			// Drop the inline override so the element is back at its
			// natural opacity (and a later physics twin clones clean).
			for (let i = 0; i < els.length; i++) els[i].style.opacity = ''
		},
	})
}

function fadeOut(scope, transitionId) {
	const ld = window.layData
	if (ld.navigation_transition_out === 'none') return
	const els = takeUnfaded(physicsElementsIn(scope), '_layNavFadeOutId', transitionId)
	if (els.length === 0) return
	anime.remove(els)
	anime({
		targets: els,
		opacity: 0,
		duration: parseInt(ld.navigation_transition_out_duration, 10) || 300,
		easing: ld.navigation_transition_out_easing || 'easeInSine',
	})
}

const drag_physics_nav_fade_controller = {
	i: function () {
		if (!window.laytheme || typeof window.laytheme.on !== 'function') return
		window.laytheme.on(ANIMATION_LIFECYCLE_EVENTS.NAVIGATION_PHASE_CHANGED, function (state) {
			if (!state) return
			if (state.phase === NAVIGATION_ANIMATION_PHASES.OUT_STARTED) {
				fadeOut(state.scope, state.transitionId)
			} else if (state.phase === NAVIGATION_ANIMATION_PHASES.IN_STARTED) {
				fadeIn(state.scope, state.transitionId)
			}
		})
	},
}

export default drag_physics_nav_fade_controller
